import clsx from 'clsx';
import type { ReactNode } from 'react';

import { Card } from '../ui';
import { formatUsdSigned } from '../../lib/format';
import type { ActivityItem } from '../../lib/types';

export interface DayBucket {
  /** Stable key — local-date string. */
  key: string;
  /** Pretty label, e.g. "Today" / "Mon, 4 May". */
  label: string;
  items: ActivityItem[];
  /** Sum of realised PnL across the day's settled trades. */
  pnl: number;
}

interface Props {
  bucket: DayBucket;
  /** Hide daily PnL summary (Wallet tab — these aren't PnL events). */
  hidePnl?: boolean;
  children: ReactNode;
}

export function DayGroup({ bucket, hidePnl, children }: Props) {
  return (
    <section>
      <div className="mb-2 flex items-baseline justify-between px-1">
        <h2 className="pb-eyebrow">{bucket.label}</h2>
        {!hidePnl ? (
          <span
            className={clsx(
              'pb-numeral text-[13px]',
              bucket.pnl > 0 && 'text-pb-success',
              bucket.pnl < 0 && 'text-pb-danger',
              bucket.pnl === 0 && 'text-tg-hint',
            )}
          >
            {formatUsdSigned(bucket.pnl)}
          </span>
        ) : null}
      </div>
      <Card pad="none" variant="flat">
        <ul className="divide-y divide-pb-border-soft">{children}</ul>
      </Card>
    </section>
  );
}

/**
 * Bucket activity items by their LOCAL date so "Today" / "Yesterday"
 * stay correct in the user's timezone. Returns groups in
 * reverse-chronological order matching item ordering.
 */
export function groupItemsByDay(
  items: ActivityItem[],
  locale: string,
): DayBucket[] {
  const map = new Map<string, DayBucket>();
  for (const it of items) {
    const d = new Date(it.created_at);
    const key = `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
    if (!map.has(key)) {
      map.set(key, {
        key,
        label: dayLabel(d, locale),
        items: [],
        pnl: 0,
      });
    }
    const b = map.get(key)!;
    b.items.push(it);
    if (it.kind === 'trade.settled') {
      const p = it.payload as { realized_pnl_usdc?: string | null };
      b.pnl += Number(p.realized_pnl_usdc ?? 0);
    }
  }
  return Array.from(map.values());
}

function dayLabel(d: Date, locale: string): string {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  const same = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
  if (same(d, today)) return locale.startsWith('ru') ? 'Сегодня' : 'Today';
  if (same(d, yesterday))
    return locale.startsWith('ru') ? 'Вчера' : 'Yesterday';
  return d.toLocaleDateString(locale, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
}
